// Resolves a human-readable value string for an ACTION step's configured input,
// e.g. "Add to Role" → the role name. Returns null when no value applies.
//
// lookups: {
//   roles:               [{ id, name }],
//   trainingModules:     [{ id, title?, name? }],
//   smallGroups:         [{ id, name }],
//   emailTemplates:      [{ id, name }],
//   boards:              [{ id, name }],
//   paths:               [{ id, name }],
//   eventInstances:      [{ id, startTime, Event: { name } }],
// }

export function resolveActionValue(step, lookups = {}) {
    if (!step || step.stepType !== 'ACTION' || !step.actionType) return null;
    const cfg = step.config || {};

    const find = (list, id, field = 'name') =>
        (list || []).find(x => x.id === id)?.[field];

    switch (step.actionType) {
        case 'ADD_TO_ROLE':
            return cfg.roleID ? find(lookups.roles, cfg.roleID) : null;

        case 'ENROLL_TRAINING':
            return cfg.trainingModuleID
                ? find(lookups.trainingModules, cfg.trainingModuleID, 'title')
                  || find(lookups.trainingModules, cfg.trainingModuleID, 'name')
                : null;

        case 'ADD_TO_SMALL_GROUP':
            return cfg.smallGroupID ? find(lookups.smallGroups, cfg.smallGroupID) : null;

        case 'SEND_EMAIL':
            return cfg.templateID ? find(lookups.emailTemplates, cfg.templateID) : null;

        case 'POST_TO_BOARD':
            return cfg.boardID ? find(lookups.boards, cfg.boardID) : null;

        case 'TRIGGER_PATH':
            return cfg.targetPathID ? find(lookups.paths, cfg.targetPathID) : null;

        case 'REGISTER_FOR_EVENT':
        case 'CHECK_INTO_EVENT': {
            if (!cfg.eventInstanceID) return null;
            const inst = (lookups.eventInstances || []).find(e => e.id === cfg.eventInstanceID);
            if (!inst) return null;
            const dateStr = inst.startTime
                ? new Date(inst.startTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                : '';
            return inst.Event?.name ? `${inst.Event.name}${dateStr ? ' — ' + dateStr : ''}` : dateStr || null;
        }

        case 'SEND_SMS':
            return cfg.message ? truncate(cfg.message, 40) : null;

        case 'REQUEST_APPROVAL': {
            const approverCount = Array.isArray(cfg.approverPersonIDs) ? cfg.approverPersonIDs.length : 0;
            const title = typeof cfg.title === 'string' ? truncate(cfg.title, 40) : null;
            return title || (approverCount ? `${approverCount} approver${approverCount === 1 ? '' : 's'}` : null);
        }

        // INVITE_TO_REACH, UPDATE_PERSON — no configurable target to display
        default:
            return null;
    }
}

const ASSIGNMENT_STRATEGY_LABELS = {
    ROUND_ROBIN: 'Round Robin',
    ORDERED: 'Ordered',
    ASSIGN_ENROLLEE: 'Assign Enrollee',
};

// Resolves actual assignment first. Before execution, ordered pools can predict
// deterministically; round-robin pools cannot because backend selection uses the
// latest assignment across the path step rather than this enrollment's position.
export function resolveTaskStepInfo(step, taskLookups = {}, enrollmentStepDetails = {}, enrollee = null) {
    if (!step || step.stepType !== 'TASK') return null;

    const taskID = step.config?.taskID || step.config?.task?.id || null;
    const strategy = step.config?.assignmentStrategy || 'ROUND_ROBIN';
    const detail = enrollmentStepDetails?.[step.id];
    const actualAssignee = detail?.assignee;
    const isWaitingOnTask = detail?.status === 'IN_PROGRESS';
    const task = taskLookups.tasks?.get(taskID) || step.config?.task || null;
    const taskName = task?.title || step.config?.taskTitle || 'Task';
    const members = (taskLookups.membersByTask?.get(taskID) || []).slice().sort((a, b) => a.position - b.position);
    let assignee = actualAssignee || null;
    let assigneePrefix = actualAssignee ? 'Assignee' : 'Next';
    let assigneeLabel = null;

    // Once a task is waiting, only its persisted TaskInstance assignment is truthful.
    if (!assignee && !isWaitingOnTask && strategy === 'ASSIGN_ENROLLEE') {
        assignee = enrollee;
        assigneePrefix = enrollee ? 'Assigned to enrollee' : null;
    } else if (!assignee && !isWaitingOnTask && strategy === 'ORDERED') {
        // No assignment means "before first"; pools may legitimately start at position 0.
        const currentPosition = detail?.currentAssigneePosition ?? Number.NEGATIVE_INFINITY;
        const member = members.find(item => item.position > currentPosition) || null;
        assignee = member?.Person || member;
    } else if (!assignee && !isWaitingOnTask && strategy === 'ROUND_ROBIN') {
        assigneeLabel = members.length === 1
            ? 'Assignment pool: 1 member'
            : `Assignment pool: ${members.length} members`;
    }

    const assigneeName = assignee
        ? `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim()
        : null;
    if (!assigneeLabel && assigneeName && assigneePrefix) assigneeLabel = `${assigneePrefix}: ${assigneeName}`;
    if (!assigneeLabel && isWaitingOnTask) assigneeLabel = 'Unassigned';
    if (!assigneeLabel && strategy === 'ASSIGN_ENROLLEE') assigneeLabel = 'Assigns the enrollee';
    if (!assigneeLabel && strategy === 'ORDERED') assigneeLabel = 'Next: Pool exhausted';
    if (!assigneeLabel) assigneeLabel = 'Assignment pool: empty';

    return {
        taskName,
        strategyLabel: ASSIGNMENT_STRATEGY_LABELS[strategy] || strategy,
        assigneeLabel,
        assigneeName,
        actualAssignee: actualAssignee || null,
        isWaitingOnTask,
    };
}

function truncate(str, max) {
    if (!str) return null;
    const clean = str.replace(/\s+/g, ' ').trim();
    return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean;
}
